import { gql } from '@apollo/client';
import { Edit, Settings } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Typography } from '@mui/material';

import { useUserOrgId } from '../../auth/authentication';
import { useAppConfig } from '../../fetching/api/appConfig';
import {
    useCreateQualification,
    useCreateQualificationOptions,
    useDeleteQualification,
    useEditQualification,
    useEditQualificationOptions,
    useListQualificationsByOrg,
} from '../../fetching/api/qualifications';
import {
    QualificationBasic,
    QualificationWithPermissions,
    useOrgPrefixedLabelForQualification,
    useOrgPrefixedLabelForQualificationGql,
} from '../../fetching/sharedSchemas/qualificationSchemas';
import { useBooleanState, useStateWithDerivedBoolean } from '../../utils/booleanState';
import { InputState, useInputState, useJointInputState } from '../../utils/inputState';
import { ReturnComponent } from '../../utils/reactTypes';
import { AxleIconButton } from '../buttons/AxleIconButton';
import { FloatingActionButton } from '../buttons/FloatingActionButton';
import { AxleInfoField } from '../displayFields/AxleInfoField';
import { QualificationChips } from '../displayFields/chips';
import {
    AxleDialog,
    AxleDialogActions,
    AxleDialogContent,
    AxleDialogDeleteButtonWithConfirmationDialog,
    AxleDialogPrimaryButton,
} from '../layout/AxleDialog';
import { AxleTable } from '../tables/AxleTable';
import { If } from '../utils/conditionalComponents';
import { FloatRight, HorizontalStackInnerGrow, VerticalSpace } from '../utils/linearLayouts';
import { AxleAutoCompleteFieldProps, AxleAutocompleteField } from './AxleAutocompleteField';
import { AxleTextField } from './AxleTextField';
import { OrgDropdown, useOrgDropdownState } from './OrgDropdown';
import { AxleAutocompleteMultiselectWithShortChips } from './multiselectFields';

const codeHelperText = (codeMaxLength: number) =>
    `Must fit in ${codeMaxLength} characters. Can include spaces.`;

const CreateQualificationDialogContent = ({
    close,
    orgToCreateOnId,
}: {
    close: () => void;
    orgToCreateOnId: string;
}): ReturnComponent => {
    const { codeMaxLength, orgOptions } = useCreateQualificationOptions();
    const createQualification = useCreateQualification();

    const orgState = useInputState<string>(orgToCreateOnId, {
        required: true,
    });
    const codeState = useInputState<string>('', {
        required: true,
        defaultHelperText: codeHelperText(codeMaxLength),
    });
    const descriptionState = useInputState<string>('');

    // The qualification model on the backend has three fields: name, code, and description.
    //  However, in practice, we just use the code field, and don’t really use the name field
    //  anywhere. So we just have the user enter the code, and use that as the name too.

    const { checkValid } = useJointInputState([orgState, codeState, descriptionState]);

    return (
        <>
            <AxleDialogContent>
                <If condition={orgOptions.length > 1}>
                    <AxleAutocompleteField
                        label={'Organization'}
                        inputState={orgState}
                        options={orgOptions}
                        optionToValue={(org) => org.uuid}
                        optionToLabel={(org) => org.name}
                    />
                </If>
                <AxleTextField label={'Code'} inputState={codeState} maxChars={codeMaxLength} />
                <AxleTextField label={'Description'} inputState={descriptionState} />
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogPrimaryButton
                    label={'Create'}
                    onClick={() => {
                        if (!checkValid()) {
                            return;
                        }
                        void createQualification(
                            {
                                org_uuid: orgState.value,
                                code: codeState.value,
                                description: descriptionState.value,
                            },
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

const CreateQualificationDialog = ({
    isOpen,
    close,
    orgToCreateOnId,
}: {
    isOpen: boolean;
    close: () => void;
    orgToCreateOnId: string;
}): ReturnComponent => {
    return (
        <AxleDialog
            dataTestId='create-qualification-dialog'
            isOpen={isOpen}
            close={close}
            title='Create Qualification'
        >
            <CreateQualificationDialogContent close={close} orgToCreateOnId={orgToCreateOnId} />
        </AxleDialog>
    );
};

const EditQualificationDialogContent = ({
    close,
    qualification,
}: {
    close: () => void;
    qualification: QualificationBasic;
}): ReturnComponent => {
    const userOrgId = useUserOrgId();
    const { codeMaxLength } = useEditQualificationOptions(qualification.uuid);
    const editQualification = useEditQualification(qualification.uuid);

    const deleteQualification = useDeleteQualification(qualification.uuid);

    const codeState = useInputState(qualification.code, {
        required: true,
        defaultHelperText: codeHelperText(codeMaxLength),
    });
    const descriptionState = useInputState(qualification.description);

    const { checkDifferent, checkValid } = useJointInputState([codeState, descriptionState]);

    return (
        <>
            <AxleDialogContent>
                <If condition={qualification.organization.uuid !== userOrgId}>
                    <AxleInfoField label={'Organization'} value={qualification.organization.name} />
                </If>
                <AxleTextField label={'Code'} inputState={codeState} maxChars={codeMaxLength} />
                <AxleTextField label={'Description'} inputState={descriptionState} />
            </AxleDialogContent>
            <AxleDialogActions>
                <AxleDialogDeleteButtonWithConfirmationDialog
                    label={'Delete'}
                    confirmationText={'Are you sure you want to delete this criteria?'}
                    onClick={() => {
                        void deleteQualification(
                            {},
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
                <AxleDialogPrimaryButton
                    label={'Save'}
                    onClick={() => {
                        if (!checkDifferent()) {
                            return;
                        }
                        if (!checkValid()) {
                            return;
                        }
                        void editQualification(
                            {
                                code: codeState.value,
                                description: descriptionState.value,
                            },
                            {
                                onSuccess: close,
                            },
                        );
                    }}
                />
            </AxleDialogActions>
        </>
    );
};

const EditQualificationDialog = ({
    isOpen,
    close,
    qualification,
}: {
    isOpen: boolean;
    close: () => void;
    qualification: QualificationBasic | null;
}): ReturnComponent => (
    <AxleDialog
        dataTestId='edit-qualifications-dialog'
        isOpen={isOpen}
        close={close}
        title='Edit Criteria'
    >
        {qualification && (
            <EditQualificationDialogContent close={close} qualification={qualification} />
        )}
    </AxleDialog>
);

export const ManageQualificationsDialogContent = ({
    close: _close,
}: {
    close: () => void;
}): ReturnComponent => {
    const appConfig = useAppConfig();

    const { qualificationsByOrg } = useListQualificationsByOrg();
    const allOrgs = Object.values(qualificationsByOrg).map((value) => value.org);

    const orgDropdownState = useOrgDropdownState({ showOptionToSelectAll: false });
    const nativeQualifications = qualificationsByOrg[orgDropdownState.value].native_qualifications;
    const passthroughQualifications =
        qualificationsByOrg[orgDropdownState.value].passthrough_qualifications;

    const [isCreateDialogOpen, openCreateDialog, closeCreateDialog] = useBooleanState(false);
    const [qualificationToEdit, setQualificationToEdit, isEditDialogOpen, closeEditDialog] =
        useStateWithDerivedBoolean<QualificationBasic>(null);

    return (
        <AxleDialogContent>
            <CreateQualificationDialog
                isOpen={isCreateDialogOpen}
                close={closeCreateDialog}
                orgToCreateOnId={orgDropdownState.value}
            />
            <EditQualificationDialog
                isOpen={isEditDialogOpen}
                close={closeEditDialog}
                qualification={qualificationToEdit}
            />

            <HorizontalStackInnerGrow spacing={2}>
                <OrgDropdown inputState={orgDropdownState} options={allOrgs} />
                <If condition={appConfig.qualification.create}>
                    <FloatRight>
                        <FloatingActionButton
                            icon={<AddIcon />}
                            text={'Create Criteria'}
                            onClick={openCreateDialog}
                        />
                    </FloatRight>
                </If>
            </HorizontalStackInnerGrow>

            <VerticalSpace spacing={2} />
            <AxleTable
                data={nativeQualifications}
                columns={[
                    {
                        label: 'Code',
                        field: 'code',
                    },
                    {
                        label: 'Description',
                        field: 'description',
                    },
                    {
                        rowToComponent: (qualification: QualificationWithPermissions) => (
                            <AxleIconButton
                                icon={<Edit />}
                                tooltip={'Edit Criteria'}
                                onClick={() => setQualificationToEdit(qualification)}
                                enabled={qualification.permissions.write}
                                tooltipWhenDisabled={'You cannot edit this criteria.'}
                            />
                        ),
                    },
                ]}
            />

            <If condition={passthroughQualifications.length > 0}>
                <VerticalSpace spacing={4} />
                <Box>
                    <Typography mb={2}>
                        These criteria were passed down to you by a different organization. You can
                        assign them to your clinicians, but you cannot edit them:
                    </Typography>
                    <QualificationChips qualifications={passthroughQualifications} />
                </Box>
            </If>
        </AxleDialogContent>
    );
};

export const ManageQualificationsDialog = ({
    isOpen,
    close,
}: {
    isOpen: boolean;
    close: () => void;
}): ReturnComponent => (
    <AxleDialog
        dataTestId='manage-eligibility-criteria-dialog'
        isOpen={isOpen}
        close={close}
        title={'Manage Eligibility Criteria'}
        width={'md'}
    >
        <ManageQualificationsDialogContent close={close} />
    </AxleDialog>
);

export const QualificationsField = ({
    label,
    enabled = true,
    description,
    inputState,
    options,
}: {
    label: string;
    enabled?: boolean;
    description?: string;
    inputState: InputState<string[]>;
    options: QualificationBasic[];
}): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualification(options);

    const [
        isManageQualificatonsDialogOpen,
        openManageQualificationsDialog,
        closeManageQualificationsDialog,
    ] = useBooleanState(false);

    return (
        <>
            <ManageQualificationsDialog
                isOpen={isManageQualificatonsDialogOpen}
                close={closeManageQualificationsDialog}
            />
            <AxleAutocompleteMultiselectWithShortChips
                label={label}
                enabled={enabled}
                inputState={inputState}
                description={description}
                options={options}
                optionToLabel={(qualification) => qualificationLabel(qualification)}
                optionToValue={(qualification) => qualification.uuid}
                optionToToolTip={(qualification) => qualification.description}
                trailingButtons={
                    <AxleIconButton
                        icon={<Settings />}
                        tooltip={'Manage Eligibility Criteria'}
                        onClick={openManageQualificationsDialog}
                    />
                }
            />
        </>
    );
};

export const QualificationField = ({
    label,
    enabled = true,
    inputState,
    options,
    ...rest
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<string | null>;
    options: QualificationBasic[];
} & Omit<
    AxleAutoCompleteFieldProps<string | null, QualificationBasic>,
    'inputState' | 'optionToLabel' | 'optionToValue'
>): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualification(options);
    return (
        <AxleAutocompleteField
            {...rest}
            label={label}
            enabled={enabled}
            inputState={inputState}
            options={options}
            optionToLabel={(qualification) => qualificationLabel(qualification)}
            optionToValue={(qualification) => qualification.uuid}
        />
    );
};

export interface IQualificationOption {
    id: string;
    name: string;
    organization: {
        id: string;
        name: string;
    };
}

export const QualificationOptionFragment = gql`
    fragment QualificationOptionFragment on Qualification {
        id
        name
        organization {
            id
            name
        }
    }
`;

export const QualificationFieldGql = ({
    label,
    enabled = true,
    inputState,
    options,
    ...rest
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<string | null>;
    options: IQualificationOption[];
} & Omit<
    AxleAutoCompleteFieldProps<string | null, IQualificationOption>,
    'inputState' | 'optionToLabel' | 'optionToValue'
>): ReturnComponent => {
    const qualificationLabel = useOrgPrefixedLabelForQualificationGql(options);
    return (
        <AxleAutocompleteField
            {...rest}
            label={label}
            enabled={enabled}
            inputState={inputState}
            options={options}
            optionToLabel={(qualification) => qualificationLabel(qualification)}
            optionToValue={(qualification) => qualification.id}
        />
    );
};

export const QualificationsFilterField = ({
    fromSchedulePage,
    inputState,
}: {
    // Where is the qualifications filter being used? This affects the label.
    // When filtering by qualifications in the visits page, we are filtering
    // by the qualifications required on the visit's service(s). When filtering
    // by qualifications in the schedules page, we are filtering by the qualifications
    // of the clinicians, not the qualifications required on the individual shifts.
    // We want to explicitly label the qualification filter on the schedule page to
    // 'Clinician Qualifications' to make it clear how we are filtering.
    fromSchedulePage: boolean;
    inputState: InputState<string[]>;
}) => {
    const userOrgId = useUserOrgId();
    const qualificationName = useOrgPrefixedLabelForQualification();

    // Load qualifications from backend.
    const { qualificationsByOrg } = useListQualificationsByOrg();
    const qualOptions = qualificationsByOrg[userOrgId].native_qualifications;

    return (
        <AxleAutocompleteMultiselectWithShortChips
            label={fromSchedulePage ? 'Clinician Qualifications' : 'Qualifications'}
            inputState={inputState}
            options={qualOptions}
            optionToValue={(option) => option.uuid}
            optionToLabel={(option) => qualificationName(option)}
        />
    );
};
